/**
 * Renders a game's config schema (introspected server-side by `core/src/configform.ts`).
 * Nothing here validates: bounds are guidance, and `configFor` parses the stored config
 * at segment start.
 */

import type { ConfigField, ConfigForm as ConfigFormSpec } from '@gsf/contract';

export interface ConfigFormProps {
  readonly form: ConfigFormSpec;
  /** The host's answers so far. A key absent means "the game's default". */
  readonly value: Record<string, unknown>;
  readonly onChange: (next: Record<string, unknown>) => void;
  readonly disabled?: boolean;
  /** Namespaces control ids: two rounds of one game would otherwise share label targets. */
  readonly idPrefix: string;
}

export function ConfigForm({ form, value, onChange, disabled, idPrefix }: ConfigFormProps) {
  if (form.fields.length === 0 && form.unsupported.length === 0) {
    return <p className="faint config-form-empty">Nothing to set up — just pick the content.</p>;
  }

  function set(key: string, next: unknown): void {
    onChange({ ...value, [key]: next });
  }

  return (
    <div className="config-form col">
      {form.fields.map((field) => (
        <Field
          key={field.key}
          id={`cfg-${idPrefix}-${field.key}`}
          field={field}
          value={value[field.key]}
          disabled={disabled === true}
          onChange={(next) => set(field.key, next)}
        />
      ))}
      {form.unsupported.length > 0 ? (
        // Say so, or the show quietly runs on a default the host never saw.
        <p className="faint">
          {form.unsupported.length} setting
          {form.unsupported.length === 1 ? '' : 's'} in this game cannot be edited here and
          will use its defaults.
        </p>
      ) : null}
    </div>
  );
}

interface FieldProps {
  readonly id: string;
  readonly field: ConfigField;
  readonly value: unknown;
  readonly disabled: boolean;
  readonly onChange: (next: unknown) => void;
}

/** One control. An unset key displays the default, since that is what will run. */
function Field({ id, field, value, disabled, onChange }: FieldProps) {

  if (field.kind === 'boolean') {
    const on = typeof value === 'boolean' ? value : field.default;
    return (
      <label className="config-row" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={on}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="col" style={{ gap: 0, minWidth: 0 }}>
          <strong>{field.label}</strong>
          {field.description ? <span className="faint">{field.description}</span> : null}
        </span>
      </label>
    );
  }

  const control =
    field.kind === 'number' ? (
      <input
        id={id}
        type="number"
        value={typeof value === 'number' ? value : field.default}
        min={field.min}
        max={field.max}
        step={field.step}
        disabled={disabled}
        onChange={(e) => {
          // Empty means mid-edit, not zero: clear the key so the default applies.
          const raw = e.target.value;
          onChange(raw === '' ? undefined : Number(raw));
        }}
      />
    ) : field.kind === 'choice' ? (
      <select
        id={id}
        value={typeof value === 'string' ? value : field.default}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    ) : (
      <input
        id={id}
        type="text"
        value={typeof value === 'string' ? value : field.default}
        maxLength={field.maxLength}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );

  return (
    <div className="field">
      <label htmlFor={id}>{field.label}</label>
      {control}
      {field.description ? <span className="faint">{field.description}</span> : null}
    </div>
  );
}
